/**
 * AR Billing — Readiness tool (ported from examples/billing_readiness.html).
 *
 * Faithful 1-to-1 port of the Softr custom-code page. The imperative render logic is preserved;
 * only the seams changed: all data goes through services/billing.js (no Airtable PAT in the
 * browser), and the Slack reminder posts through the Clerk-gated /api/billing-remind (no bot token
 * in the browser). All DOM is scoped to `root` (queries, appended modals, delegated listeners) so
 * React unmount tears everything down cleanly.
 */

import * as B from '../services/billing.js'
import { alertDialog } from '../components/Dialog.jsx'

const CSS = `
#aj-ready *{box-sizing:border-box}
#aj-ready,.aj-modal-overlay,.aj-spinner-overlay{
  --aj:#07272D;--aj-mid:#0a3540;--bg:#f4f7f8;--surf:#edf2f3;--wh:#ffffff;
  --t1:#07272D;--t2:#3a5a60;--t3:#6b8f95;--t4:#9ab8bd;
  --b1:rgba(7,39,45,0.08);--b2:rgba(7,39,45,0.14);--b3:rgba(7,39,45,0.22);
  --red:#C0392B;--red-bg:#fdf0ef;--amb:#B7651A;--amb-bg:#fdf4e7;--grn:#1a7a4a;--grn-bg:#eaf4ee;
  --blu:#1a4f8a;--blu-bg:#edf3fb;--r4:4px;--r8:8px;--r10:10px}
/* Dark mode: remap the local palette (values mirror the global --aj-* tokens in outline.css).
   The modal + spinner overlays are appended as SIBLINGS of #aj-ready, so they carry the tokens
   too. Brand-dark bands (--aj), solid brand buttons, and danger red stay constant. */
body.dark #aj-ready,body.dark .aj-modal-overlay,body.dark .aj-spinner-overlay{
  --bg:#0d1e24;--surf:#1c3840;--wh:#1b3a44;
  --t1:#cfe2e8;--t2:#8ab0bc;--t3:#5c8494;--t4:#4a6d7c;
  --b1:rgba(255,255,255,0.07);--b2:rgba(255,255,255,0.10);--b3:rgba(255,255,255,0.16);
  --red-bg:rgba(192,57,43,0.16);--amb:#d08a3e;--amb-bg:rgba(183,101,26,0.16);
  --grn:#3fae7e;--grn-bg:rgba(26,122,74,0.16);--blu:#5b96d6;--blu-bg:rgba(26,79,138,0.18)}
#aj-ready{font-family:'DM Sans',sans-serif;font-size:13px;color:var(--t1);padding:1.25rem;max-width:1100px;margin:0 auto}
.aj-countdown{background:var(--aj);border-radius:var(--r10);padding:1rem 1.25rem;margin-bottom:1.25rem;display:flex;align-items:center;gap:1.5rem;flex-wrap:wrap}
.aj-cd-block{display:flex;flex-direction:column}
.aj-cd-lbl{font-family:'Montserrat',sans-serif;font-weight:500;font-size:9px;letter-spacing:0.1em;text-transform:uppercase;color:rgba(255,255,255,0.4);margin-bottom:3px}
.aj-cd-val{font-family:'DM Mono',monospace;font-size:26px;font-weight:500;color:white;line-height:1}
.aj-cd-sub{font-family:'DM Mono',monospace;font-size:10px;color:rgba(255,255,255,0.4);margin-top:3px}
.aj-cd-sep{width:0.5px;height:40px;background:rgba(255,255,255,0.12)}
.aj-cd-right{margin-left:auto}
.aj-btn{font-size:11px;padding:6px 13px;border-radius:var(--r4);border:0.5px solid var(--b3);background:transparent;color:var(--t1);cursor:pointer;font-family:'DM Sans',sans-serif;white-space:nowrap;transition:background 0.12s}
.aj-btn:hover{background:var(--surf)}
.aj-btn-p{background:var(--aj);border-color:var(--aj);color:white}
.aj-btn-p:hover{background:var(--aj-mid)}
.aj-btn-ghost{background:rgba(255,255,255,0.1);border-color:rgba(255,255,255,0.2);color:white}
.aj-btn-ghost:hover{background:rgba(255,255,255,0.18)}
.aj-btn-warn{border-color:var(--amb);color:var(--amb)}
.aj-btn-warn:hover{background:var(--amb-bg)}
.aj-btn-sm{font-size:10px;padding:4px 10px}
.aj-btn-xs{font-size:10px;padding:3px 8px}
.aj-stat-row{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:8px;margin-bottom:1.25rem}
.aj-stat{background:var(--wh);border:0.5px solid var(--b1);border-radius:var(--r10);padding:0.75rem 0.875rem}
.aj-stat-val{font-family:'DM Mono',monospace;font-size:20px;font-weight:500;line-height:1}
.aj-stat-lbl{font-size:10px;color:var(--t3);margin-top:3px;font-family:'Montserrat',sans-serif;font-weight:500;letter-spacing:0.04em;text-transform:uppercase}
.aj-two{display:grid;grid-template-columns:1fr 320px;gap:1rem;align-items:start}
.aj-eyebrow-row{display:flex;align-items:center;justify-content:space-between;margin-bottom:8px}
.aj-eyebrow{font-family:'Montserrat',sans-serif;font-weight:500;font-size:9px;letter-spacing:0.1em;text-transform:uppercase;color:var(--t3)}
.aj-card{background:var(--wh);border:0.5px solid var(--b1);border-radius:var(--r10);overflow:hidden;margin-bottom:1rem}
.aj-prow{display:flex;align-items:center;gap:10px;padding:0.625rem 0.875rem;border-bottom:0.5px solid var(--b1)}
.aj-prow:last-child{border-bottom:none}
.aj-av{width:32px;height:32px;border-radius:50%;background:var(--surf);display:flex;align-items:center;justify-content:center;font-family:'DM Mono',monospace;font-size:10px;font-weight:500;color:var(--t2);flex-shrink:0}
.aj-prog-track{height:4px;background:var(--surf);border-radius:2px;overflow:hidden;flex:1;max-width:100px}
.aj-prog-fill{height:100%;border-radius:2px;transition:width 0.3s}
.aj-pill{font-size:10px;font-weight:500;padding:2px 8px;border-radius:20px;white-space:nowrap;display:inline-block}
.p-grn{background:var(--grn-bg);color:var(--grn)}
.p-amb{background:var(--amb-bg);color:var(--amb)}
.p-red{background:var(--red-bg);color:var(--red)}
.p-blu{background:var(--blu-bg);color:var(--blu)}
.p-muted{background:var(--surf);color:var(--t3)}
.aj-banner{background:var(--wh);border:0.5px solid var(--b1);border-left:3px solid var(--amb);border-radius:0 var(--r10) var(--r10) 0;padding:0.625rem 0.875rem;display:flex;align-items:center;gap:10px;margin-bottom:1rem}
.aj-banner.red{border-left-color:var(--red)}
.aj-banner-text{font-size:12px;color:var(--t2);flex:1;line-height:1.5}
.aj-banner-text strong{color:var(--t1);font-weight:500}
.aj-mono{font-family:'DM Mono',monospace}
.aj-snap-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;padding:0.875rem}
.aj-snap-item{text-align:center}
.aj-snap-val{font-family:'DM Mono',monospace;font-size:17px;font-weight:500}
.aj-snap-lbl{font-size:10px;color:var(--t3);margin-top:2px}
.aj-run-row{display:flex;justify-content:space-between;align-items:center;padding:5px 0;font-size:11px;border-bottom:0.5px solid var(--b1)}
.aj-run-row:last-child{border-bottom:none}
.aj-loading{padding:2rem;text-align:center;color:var(--t3);font-size:12px}
.aj-tooltip-wrap{position:relative;display:inline-block}
.aj-tooltip-wrap .aj-tooltip{visibility:hidden;opacity:0;background:#07272D;color:white;font-size:10px;padding:5px 8px;border-radius:4px;position:absolute;bottom:calc(100% + 6px);left:50%;transform:translateX(-50%);white-space:nowrap;pointer-events:none;transition:opacity 0.15s;z-index:100;font-family:'DM Sans',sans-serif}
.aj-tooltip-wrap .aj-tooltip:after{content:'';position:absolute;top:100%;left:50%;transform:translateX(-50%);border:4px solid transparent;border-top-color:#07272D}
.aj-tooltip-wrap:hover .aj-tooltip{visibility:visible;opacity:1}
.aj-modal-overlay{position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.45);display:flex;align-items:center;justify-content:center;z-index:9998}
.aj-modal{background:var(--wh);border-radius:10px;width:480px;max-width:92vw;overflow:hidden;box-shadow:0 8px 32px rgba(0,0,0,0.25)}
.aj-modal-head{background:#07272D;padding:0.875rem 1.1rem;display:flex;align-items:center;justify-content:space-between}
.aj-modal-title{font-family:'Montserrat',sans-serif;font-weight:300;font-size:13px;letter-spacing:0.08em;text-transform:uppercase;color:white}
.aj-modal-close{color:rgba(255,255,255,0.5);cursor:pointer;font-size:18px;line-height:1;background:none;border:none;padding:0}
.aj-modal-body{padding:1rem 1.1rem}
.aj-modal-label{font-size:10px;color:var(--t3);font-family:'Montserrat',sans-serif;font-weight:500;letter-spacing:0.08em;text-transform:uppercase;margin-bottom:5px}
.aj-modal-textarea{width:100%;font-size:12px;line-height:1.6;padding:8px 10px;border-radius:4px;border:0.5px solid var(--b3);font-family:'DM Sans',sans-serif;background:var(--wh);color:var(--t1);resize:vertical;min-height:120px}
.aj-modal-footer{padding:0.75rem 1.1rem;border-top:0.5px solid var(--b1);display:flex;gap:8px;justify-content:flex-end}
.aj-modal-status{font-size:11px;color:var(--t3);margin-right:auto;align-self:center}
.aj-error{padding:1rem;background:var(--red-bg);border-radius:var(--r8);color:var(--red);font-size:12px;margin-bottom:1rem}
.aj-spinner-overlay{position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.45);display:flex;align-items:center;justify-content:center;z-index:9999}
.aj-spinner-box{background:var(--wh);border-radius:var(--r10);padding:1.5rem 2rem;display:flex;flex-direction:column;align-items:center;gap:12px;min-width:220px}
.aj-spinner{width:28px;height:28px;border:2.5px solid var(--surf);border-top-color:var(--aj);border-radius:50%;animation:aj-spin 0.7s linear infinite}
@keyframes aj-spin{to{transform:rotate(360deg)}}
.aj-spinner-label{font-size:12px;color:var(--t2);font-family:'DM Sans',sans-serif;text-align:center}
.aj-spinner-sub{font-size:10px;color:var(--t3);font-family:'DM Mono',monospace;text-align:center}
@media(max-width:780px){.aj-two{grid-template-columns:1fr}.aj-stat-row{grid-template-columns:1fr 1fr}.aj-cd-sep{display:none}}
`

export function mountReadiness(root) {
  const styleEl = document.createElement('style')
  styleEl.textContent = CSS
  root.appendChild(styleEl)
  root.insertAdjacentHTML('beforeend', '<div id="aj-ready"><div id="aj-ready-inner"><div class="aj-loading">Loading...</div></div></div>')

  const $ = (sel) => root.querySelector(sel)
  const state = { run: null, priorRun: null, lineItems: [], members: [], expenses: [], entries: [], reminded: {}, policyMap: {}, settings: null, holidays: [], billingHolds: [], billingPeriod: null }

  function getCurrentMonthYear() {
    var now = new Date()
    var months = ['January','February','March','April','May','June','July','August','September','October','November','December']
    return { month: months[now.getMonth()], year: String(now.getFullYear()) }
  }

  // The month immediately before the current one — used to gate creating this month's run until
  // the prior one is closed out. Independent copy of the month math above (this file already
  // duplicates runTool.js's own getMonthYear(offset) rather than sharing a module).
  function getPriorMonthYear() {
    var now = new Date()
    var months = ['January','February','March','April','May','June','July','August','September','October','November','December']
    var d = new Date(now.getFullYear(), now.getMonth() - 1, 1)
    return { month: months[d.getMonth()], year: String(d.getFullYear()) }
  }

  function daysUntilLastFriday() {
    var now = new Date()
    var year = now.getFullYear(), month = now.getMonth()
    var lastDay = new Date(year, month + 1, 0)
    while (lastDay.getDay() !== 5) lastDay.setDate(lastDay.getDate() - 1)
    var diff = Math.ceil((lastDay - now) / 86400000)
    return Math.max(0, diff)
  }

  // The T+E review window itself now lives in services/billing.js (getBillingPeriod, "since last
  // run" — see its doc comment) so runTool.js's per-project insight uses the exact same "since the
  // last run's own window ended" logic instead of two independently-drifting one-month lookbacks.
  // Computed once per load() into state.billingPeriod; getMemberStats() and render() both read
  // that instead of recomputing (it now requires an async run-history lookup).

  function getWorkingDays(start, end) {
    var days = [], d = new Date(start)
    while (d <= end) {
      var dow = d.getDay()
      if (dow !== 0 && dow !== 6) days.push(new Date(d))
      d.setDate(d.getDate() + 1)
    }
    return days
  }

  function dailyMin() { return (state.settings && Number(state.settings.full_time_daily_min)) || 8 }
  function isHoliday(d) { return state.holidays.indexOf(d.toISOString().split('T')[0]) !== -1 }
  // 'hours' = full-time employee, gated on hours. 'attest'/'none' = no hours target (contractors,
  // part-timers, or anyone without a time policy) — never counted "behind" by hours.
  function memberMode(memberId) {
    var p = state.policyMap[memberId]
    if (!p || p.enforced === false) return 'none'
    return (p.employment_type === 'employee' && p.schedule === 'full_time') ? 'hours' : 'attest'
  }

  function getMemberStats(memberId) {
    var mode = memberMode(memberId)
    var period = state.billingPeriod || { start: new Date(), end: new Date() }
    var workingDays = getWorkingDays(period.start, period.end).filter(function(d) { return !isHoliday(d) })
    var totalExpected = mode === 'hours' ? workingDays.length * dailyMin() : 0
    var myEntries = state.entries.filter(function(e) { return (e.fields['Submitter'] || [])[0] === memberId })
    var approvedStatuses = { 'Submitted': true, 'Approved': true, 'Posted': true }
    var submitted = myEntries.filter(function(e) { return approvedStatuses[e.fields['Status']] })
    var hours = submitted.reduce(function(s, e) { return s + (e.fields['Actual Hours'] || 0) }, 0)
    var rejected = myEntries.filter(function(e) { return e.fields['Status'] === 'Rejected' }).length
    var pending = myEntries.filter(function(e) { return e.fields['Status'] === 'Pending Review' }).length
    var coveredDays = {}
    submitted.forEach(function(e) { if (e.fields['Entry Date']) coveredDays[e.fields['Entry Date']] = true })
    var missingDays = mode === 'hours' ? workingDays.filter(function(d) { return !coveredDays[d.toISOString().split('T')[0]] }).length : 0
    var pct = mode === 'hours' ? (totalExpected > 0 ? Math.min(100, Math.round(hours / totalExpected * 100)) : 100) : null
    // exempt members never read as "behind" by hours; full-time members must hit hours with nothing hanging.
    var allGreen = mode === 'hours' ? (pct >= 95 && rejected === 0 && pending === 0 && missingDays === 0) : true
    var latestDate = null
    submitted.forEach(function(e) {
      if (e.fields['Entry Date'] && (!latestDate || e.fields['Entry Date'] > latestDate)) latestDate = e.fields['Entry Date']
    })
    return { mode: mode, hours: hours, expected: totalExpected, pct: pct, rejected: rejected, pending: pending, missingDays: missingDays, allGreen: allGreen, latestDate: latestDate }
  }

  // ── Data load (service layer) ────────────────────────────────────────────────
  async function load() {
    var my = getCurrentMonthYear()
    var run = await B.getRunForMonth(my.month, my.year)
    // Team time-entry readiness, reimbursables, and the billing period are all pre-run-creation
    // signals — the whole point of Readiness is deciding whether it's time to create the run, so
    // they must load even when no run exists yet. Previously this bailed out entirely with an
    // empty render() before any of that was fetched, making the screen look broken exactly when
    // it's needed most (right before creating next month's run).
    if (!run) {
      var prior = getPriorMonthYear()
      state.priorRun = await B.getRunForMonth(prior.month, prior.year)
    }
    state.run = run || null
    var period = await B.getBillingPeriod()
    state.billingPeriod = period
    var startISO = period.start.toISOString().split('T')[0]
    var endISO = period.end.toISOString().split('T')[0]
    var results = await Promise.all([
      run ? B.getLineItemsForRun(run.id) : Promise.resolve([]),
      B.getBillingMembers(),
      B.getTimeEntriesInPeriod(startISO, endISO),
      B.getReimbursables(),
      B.getTimePolicy(),
    ])
    state.lineItems = results[0] || []
    state.members = results[1] || []
    state.entries = results[2] || []
    var pol = results[4] || { members: [], settings: null, holidays: [] }
    state.settings = pol.settings
    state.holidays = pol.holidays || []
    state.policyMap = {}
    ;(pol.members || []).forEach(function(pm) { if (pm && pm.id) state.policyMap[pm.id] = pm.policy || null })
    state.expenses = (results[3] || []).filter(function(e) {
      var s = e.fields['Workflow Status'] || ''
      return s !== '' && s !== 'Logged into Core'
    })
    render(state.run)
  }

  function render(run) {
    var h = ''
    var my = getCurrentMonthYear()
    var daysLeft = daysUntilLastFriday()

    var hoursMembers = state.members.filter(function(m) { return getMemberStats(m.id).mode === 'hours' })
    var behind = hoursMembers.filter(function(m) { return !getMemberStats(m.id).allGreen })
    var currentCount = hoursMembers.length - behind.length
    // 'Logged into Core' was never a Workflow Status choice (it's the name of a formula field on that
    // table) — this comparison matched nothing, so every already-recorded expense counted as pending.
    // And Total Cost reads $0 for any expense without Units. Both fixed via services/billing.js.
    var pendingExp = state.expenses.filter(function(e) { var s = e.fields['Workflow Status'] || ''; return s !== B.EXPENSE_IN_CORE_STATUS && s !== '' })
    var totalPending = 0
    pendingExp.forEach(function(e) { totalPending += B.expenseAmount(e) })
    var li = state.lineItems
    var readyCount = li.filter(function(x) { return x.fields['Ready for Batch Invoice?'] === 'Yes' }).length
    var postedCount = li.filter(function(x) { return x.fields['Mark Posted'] }).length
    var skipCount = li.filter(function(x) { return (x.fields['Skip Invoicing?'] || '').indexOf('Skip') > -1 }).length
    var totalLI = li.length

    h += '<div class="aj-countdown">'
    var lastFriday = new Date(); lastFriday.setDate(1); lastFriday.setMonth(lastFriday.getMonth() + 1); lastFriday.setDate(0)
    while (lastFriday.getDay() !== 5) lastFriday.setDate(lastFriday.getDate() - 1)
    var lastFridayStr = lastFriday.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    h += '<div class="aj-cd-block"><div class="aj-cd-lbl">Days to close ' + my.month + ' run</div>'
    h += '<div class="aj-cd-val">' + daysLeft + '</div>'
    h += '<div class="aj-cd-sub">' + lastFridayStr + '</div></div>'
    h += '<div class="aj-cd-sep"></div>'
    h += '<div class="aj-cd-block"><div class="aj-cd-lbl">Target issuance</div>'
    h += '<div class="aj-cd-val" style="font-size:18px;color:rgba(255,255,255,0.8)">Tuesday 10am</div>'
    h += '<div class="aj-cd-sub">Following Monday close</div></div>'
    h += '<div class="aj-cd-sep"></div>'
    h += '<div class="aj-cd-block"><div class="aj-cd-lbl">Billing run</div>'
    h += '<div class="aj-cd-val" style="font-size:16px;color:rgba(255,255,255,0.8)">' + my.month + ' ' + my.year + '</div>'
    h += '<div class="aj-cd-sub">' + (run ? (run.fields['Status'] || 'No run found') : 'No active run') + '</div></div>'
    h += '<div class="aj-cd-sep"></div>'
    var bp = state.billingPeriod || { start: new Date(), end: new Date() }
    var bpStartStr = bp.start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    var bpEndStr = bp.end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    h += '<div class="aj-cd-block"><div class="aj-cd-lbl">Billing period</div>'
    h += '<div class="aj-cd-val" style="font-size:15px;color:rgba(255,255,255,0.8)">' + bpStartStr + ' - ' + bpEndStr + '</div>'
    h += '<div class="aj-cd-sub">T+E coverage window</div></div>'
    h += '<div class="aj-cd-right">'
    if (run) {
      h += '<button class="aj-btn aj-btn-ghost" data-action="open-run" data-run-id="' + run.id + '">Open billing run -></button>'
    } else if (state.priorRun && state.priorRun.fields['Status'] !== 'Complete') {
      h += '<button class="aj-btn aj-btn-ghost" data-action="open-run" data-run-id="' + state.priorRun.id + '" style="opacity:0.6" title="Close out ' + (state.priorRun.fields['Month'] || 'the prior') + ' first">Close out ' + (state.priorRun.fields['Month'] || 'prior month') + ' first &#8594;</button>'
    } else {
      h += '<button class="aj-btn aj-btn-ghost" data-action="create-run">Create ' + my.month + ' run</button>'
    }
    h += '</div></div>'

    if (behind.length > 0) {
      h += '<div class="aj-banner"><div class="aj-banner-text"><strong>' + behind.length + ' team member' + (behind.length > 1 ? 's are' : ' is') + ' behind on time entries.</strong> Billing cannot close accurately until all entries are current.</div>'
      h += '<button class="aj-btn aj-btn-warn aj-btn-sm">Remind all</button></div>'
    }

    h += '<div class="aj-stat-row">'
    h += '<div class="aj-stat"><div class="aj-stat-val">' + totalLI + '</div><div class="aj-stat-lbl">Projects in run</div></div>'
    h += '<div class="aj-stat"><div class="aj-stat-val" style="color:var(--grn)">' + currentCount + '</div><div class="aj-stat-lbl">T+E current</div></div>'
    h += '<div class="aj-stat"><div class="aj-stat-val" style="color:' + (behind.length > 0 ? 'var(--amb)' : 'var(--grn)') + '">' + behind.length + '</div><div class="aj-stat-lbl">T+E behind</div></div>'
    h += '<div class="aj-stat"><div class="aj-stat-val" style="color:' + (pendingExp.length > 0 ? 'var(--amb)' : 'var(--grn)') + '">' + pendingExp.length + '</div><div class="aj-stat-lbl">Reimbursables pending</div></div>'
    h += '</div>'

    h += '<div class="aj-two">'
    h += '<div>'

    h += '<div class="aj-eyebrow-row"><div class="aj-eyebrow">Time entry status</div>'
    h += '<button class="aj-btn aj-btn-sm">Remind all behind</button></div>'
    h += '<div class="aj-card">'
    if (state.members.length === 0) {
      h += '<div style="padding:1rem;font-size:12px;color:var(--t3)">No team members found.</div>'
    } else {
      state.members.forEach(function(m) {
        var stats = getMemberStats(m.id)
        var exempt = stats.mode !== 'hours'
        var c = stats.allGreen ? 'var(--grn)' : stats.pct < 50 ? 'var(--red)' : 'var(--amb)'
        var initials = (m.fields['Member Name'] || '?').split(' ').map(function(x){return x[0]}).join('').substring(0,2)
        var subLine = exempt ? (stats.hours.toFixed(1) + 'h logged · no hours target') : (stats.hours.toFixed(1) + 'h of ' + stats.expected + 'h expected')
        var flags = []
        if (stats.rejected > 0) flags.push('<span style="font-size:9px;background:var(--red-bg);color:var(--red);padding:1px 5px;border-radius:3px">' + stats.rejected + ' rejected</span>')
        if (!exempt && stats.pending > 0) flags.push('<span style="font-size:9px;background:var(--amb-bg);color:var(--amb);padding:1px 5px;border-radius:3px">' + stats.pending + ' pending</span>')
        if (!exempt && stats.missingDays > 0) flags.push('<span style="font-size:9px;background:var(--surf);color:var(--t3);padding:1px 5px;border-radius:3px">' + stats.missingDays + ' day' + (stats.missingDays > 1 ? 's' : '') + ' missing</span>')
        h += '<div class="aj-prow">'
        h += '<div class="aj-av" style="background:' + (exempt ? 'var(--surf)' : (stats.allGreen ? 'var(--grn-bg)' : 'var(--surf)')) + ';color:' + (exempt ? 'var(--t4)' : (stats.allGreen ? 'var(--grn)' : 'var(--t2)')) + '">' + initials + '</div>'
        h += '<div style="flex:1;min-width:0">'
        h += '<div style="font-size:12px;font-weight:500">' + (m.fields['Member Name'] || '') + '</div>'
        h += '<div style="display:flex;align-items:center;gap:4px;margin-top:3px;flex-wrap:wrap">'
        h += '<span style="font-size:10px;color:var(--t3)">' + subLine + '</span>'
        if (flags.length > 0) h += flags.join('')
        h += '</div></div>'
        if (exempt) {
          h += '<div class="aj-mono" style="font-size:10px;color:var(--t4);min-width:118px;text-align:right">no hours target</div>'
        } else {
          h += '<div class="aj-prog-track" style="max-width:80px"><div class="aj-prog-fill" style="width:' + stats.pct + '%;background:' + c + '"></div></div>'
          h += '<div class="aj-mono" style="font-size:11px;min-width:34px;text-align:right;color:' + c + '">' + stats.pct + '%</div>'
        }
        var daysAgo = 0
        if (stats.latestDate) { daysAgo = Math.floor((new Date() - new Date(stats.latestDate)) / 86400000) }
        var daysLabel = stats.latestDate ? (daysAgo === 0 ? 'today' : daysAgo + 'd ago') : 'no entries'
        h += '<div style="font-size:10px;color:var(--t4);margin-left:6px;font-family:DM Mono,monospace">' + daysLabel + '</div>'
        var mname = m.fields['Member Name'] || ''
        var alreadyReminded = state.reminded[m.id] ? true : false
        var btnDisabled = stats.allGreen || alreadyReminded
        var tipMsg = exempt ? 'No hours target' : stats.allGreen ? 'Entries are current' : alreadyReminded ? 'Reminder sent this session' : ''
        h += '<div class="aj-tooltip-wrap" style="margin-left:8px">'
        if (tipMsg) h += '<div class="aj-tooltip">' + tipMsg + '</div>'
        if (btnDisabled) {
          h += '<button class="aj-btn aj-btn-xs" style="opacity:0.3;cursor:not-allowed">Remind</button>'
        } else {
          h += '<button class="aj-btn aj-btn-xs aj-btn-warn" data-action="remind-member" data-member-id="' + m.id + '" data-member-name="' + mname + '" data-days="' + daysAgo + '">Remind</button>'
        }
        h += '</div></div>'
      })
    }
    h += '</div>'

    h += '<div class="aj-eyebrow-row"><div class="aj-eyebrow">Reimbursables pending billing</div>'
    h += '<button class="aj-btn aj-btn-sm aj-btn-p" style="opacity:0.5;cursor:default" title="Log reimbursable — coming soon">+ Log reimbursable</button></div>'
    h += '<div class="aj-card">'
    if (pendingExp.length === 0) {
      h += '<div style="padding:1rem;font-size:12px;color:var(--t3)">No pending reimbursables.</div>'
    } else {
      pendingExp.forEach(function(e) {
        h += '<div class="aj-prow">'
        h += '<div style="flex:1;min-width:0"><div style="font-size:12px;font-weight:500">' + (e.fields['Name'] || '') + '</div>'
        h += '<div style="font-size:10px;color:var(--t3)">' + (e.fields['Expense Type'] || '') + '</div></div>'
        h += '<div class="aj-mono" style="font-size:12px;margin-right:10px">$' + B.expenseAmount(e).toLocaleString() + '</div>'
        h += '<span class="aj-pill p-amb">pending</span>'
        h += '</div>'
      })
    }
    h += '<div style="padding:0.5rem 0.875rem;border-top:0.5px solid var(--b1);display:flex;justify-content:space-between;align-items:center">'
    h += '<div style="font-size:11px;color:var(--t3)">Total pending</div>'
    h += '<div class="aj-mono" style="font-size:13px;font-weight:500">$' + totalPending.toLocaleString() + '</div>'
    h += '</div></div>'
    h += '</div>'

    h += '<div>'
    h += '<div class="aj-eyebrow" style="margin-bottom:8px">AR snapshot</div>'
    h += '<div class="aj-card" style="margin-bottom:1rem">'
    h += '<div class="aj-snap-grid">'
    h += '<div class="aj-snap-item"><div class="aj-snap-val" id="ar-out">…</div><div class="aj-snap-lbl">Outstanding</div></div>'
    h += '<div class="aj-snap-item"><div class="aj-snap-val" id="ar-60" style="color:var(--red)">…</div><div class="aj-snap-lbl">60+ days</div></div>'
    h += '<div class="aj-snap-item"><div class="aj-snap-val" id="ar-30" style="color:var(--amb)">…</div><div class="aj-snap-lbl">30-60 days</div></div>'
    h += '<div class="aj-snap-item"><div class="aj-snap-val" id="ar-cur" style="color:var(--grn)">…</div><div class="aj-snap-lbl">Current</div></div>'
    h += '</div>'
    h += '<div id="ar-note" style="padding:0.5rem 0.875rem;border-top:0.5px solid var(--b1);font-size:10px;color:var(--t4);font-style:italic">Loading open balances from BQE Core…</div>'
    h += '</div>'

    h += '<div class="aj-eyebrow" style="margin-bottom:8px">Billing holds</div>'
    h += '<div class="aj-card" style="margin-bottom:1rem"><div id="aj-holds-body">'
    h += '<div style="padding:0.875rem;font-size:11px;color:var(--t4);font-style:italic">Loading Client Hub balances…</div>'
    h += '</div></div>'

    h += '<div class="aj-eyebrow" style="margin-bottom:8px">Billing run status</div>'
    h += '<div class="aj-card">'
    h += '<div style="padding:0.875rem">'
    h += '<div class="aj-run-row"><span style="color:var(--t3)">Ready for batch</span><span class="aj-mono" style="color:var(--blu)">' + readyCount + '</span></div>'
    h += '<div class="aj-run-row"><span style="color:var(--t3)">Posted</span><span class="aj-mono" style="color:var(--grn)">' + postedCount + '</span></div>'
    h += '<div class="aj-run-row"><span style="color:var(--t3)">Skipping</span><span class="aj-mono" style="color:var(--t3)">' + skipCount + '</span></div>'
    h += '<div class="aj-run-row"><span style="color:var(--t3)">Total projects</span><span class="aj-mono">' + totalLI + '</span></div>'
    h += '</div>'
    var donePct = totalLI > 0 ? Math.round(postedCount / totalLI * 100) : 0
    h += '<div style="padding:0 0.875rem 0.75rem">'
    h += '<div style="height:3px;background:var(--surf);border-radius:2px;overflow:hidden"><div style="height:100%;width:' + donePct + '%;background:var(--grn);border-radius:2px"></div></div>'
    h += '<div class="aj-mono" style="font-size:10px;color:var(--t3);margin-top:4px">' + donePct + '% posted</div>'
    h += '</div>'
    h += '<div style="padding:0.5rem 0.875rem;border-top:0.5px solid var(--b1)">'
    h += '<button class="aj-btn aj-btn-sm aj-btn-p" data-action="open-run" data-run-id="' + (state.run ? state.run.id : '') + '">Open billing run -></button>'
    h += '</div></div>'
    h += '</div>'
    h += '</div>'

    $('#aj-ready-inner').innerHTML = h
    fillArSnapshot()
    fillBillingHolds()
  }

  // Fill the AR snapshot from the firm-wide Core invoice cache (open balances by age). Async so the
  // readiness screen isn't blocked by the invoice load; cached after the first pull.
  async function fillArSnapshot() {
    var set = function(id, v) { var el = $('#' + id); if (el) el.textContent = v }
    var fmt = function(n) { return n >= 1000 ? '$' + Math.round(n / 1000) + 'k' : '$' + Math.round(n || 0) }
    try {
      var ar = await B.getArSnapshot()
      if (!ar || !ar.hasData) {
        set('ar-out', '—'); set('ar-60', '—'); set('ar-30', '—'); set('ar-cur', '—')
        var n0 = $('#ar-note'); if (n0) n0.textContent = 'Open balances not available from Core.'
        return
      }
      set('ar-out', fmt(ar.outstanding)); set('ar-60', fmt(ar.d60plus)); set('ar-30', fmt(ar.d30_60)); set('ar-cur', fmt(ar.current))
      var note = $('#ar-note'); if (note) note.textContent = 'Live from BQE Core · open invoice balances by age'
    } catch (e) {
      var n1 = $('#ar-note'); if (n1) n1.textContent = 'Could not load AR from Core.'
    }
  }

  var TIER_PILL = { hold: 'p-red', approaching: 'p-amb', ok: 'p-grn' }
  var TIER_LABEL = { hold: 'On hold', approaching: 'Approaching', ok: 'Current' }

  function fmtHoldDate(iso) {
    if (!iso) return ''
    var d = new Date(iso)
    return isNaN(d) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  // Fill the staff-facing billing-hold list — which clients are locked out of their journey (or
  // trending toward it) for non-payment. Reads the same firm-wide Core invoice cache the AR
  // snapshot uses (via api/billing-holds.js), scoped to projects with an active Client Hub.
  async function fillBillingHolds() {
    var body = $('#aj-holds-body')
    if (!body) return
    try {
      var res = await B.getBillingHolds()
      if (res && res.error) {
        body.innerHTML = '<div style="padding:0.875rem;font-size:11px;color:var(--t3)">Could not load billing holds from Core.</div>'
        return
      }
      var holds = (res && res.holds) || []
      state.billingHolds = holds
      if (!holds.length) {
        body.innerHTML = '<div style="padding:0.875rem;font-size:11px;color:var(--t3)">No open client balances right now.</div>'
        return
      }
      var h = ''
      holds.forEach(function (hold, i) {
        h += '<div class="aj-prow">'
        h += '<div style="flex:1;min-width:0">'
        h += '<div style="font-size:12px;font-weight:500">' + (hold.projectNumber ? hold.projectNumber + ' ' : '') + (hold.projectName || 'Unknown project') + '</div>'
        h += '<div style="font-size:10px;color:var(--t3);margin-top:1px">' + hold.daysOverdue + ' days overdue &middot; ' + hold.unpaidCount + ' open invoice' + (hold.unpaidCount > 1 ? 's' : '') + (hold.lastReminderAt ? ' &middot; reminded ' + fmtHoldDate(hold.lastReminderAt) : '') + '</div>'
        h += '</div>'
        h += '<span class="aj-pill ' + (TIER_PILL[hold.tier] || 'p-muted') + '" style="margin-right:8px">' + (TIER_LABEL[hold.tier] || hold.tier) + '</span>'
        h += '<button class="aj-btn aj-btn-xs" data-action="open-billing-remind" data-hold-idx="' + i + '">Remind</button>'
        h += '</div>'
      })
      body.innerHTML = h
    } catch (e) {
      body.innerHTML = '<div style="padding:0.875rem;font-size:11px;color:var(--t3)">Could not load billing holds.</div>'
    }
  }

  // ── Billing reminder modal (preview-then-send; reuses api/billing-send-invoice.js) ─────────────
  function showBillingReminderModal(holdIdx) {
    var existing = root.querySelector('#aj-billing-remind-modal')
    if (existing) existing.parentNode.removeChild(existing)
    var hold = state.billingHolds[holdIdx]
    if (!hold) return

    var el = document.createElement('div')
    el.id = 'aj-billing-remind-modal'; el.className = 'aj-modal-overlay'
    el.innerHTML = '<div class="aj-modal">'
      + '<div class="aj-modal-head"><div class="aj-modal-title">Remind ' + (hold.projectName || 'client') + '</div><button class="aj-modal-close" data-action="close-billing-remind-modal">x</button></div>'
      + '<div class="aj-modal-body">'
      + '<div id="aj-billing-remind-preview" style="font-size:11px;color:var(--t3);font-style:italic">Loading preview…</div>'
      + '</div>'
      + '<div class="aj-modal-footer">'
      + '<span class="aj-modal-status" id="aj-billing-remind-status"></span>'
      + '<button class="aj-btn" data-action="close-billing-remind-modal">Cancel</button>'
      + '<button class="aj-btn aj-btn-p" data-action="send-billing-remind" data-hold-idx="' + holdIdx + '" disabled>Send reminder</button>'
      + '</div></div>'
    root.appendChild(el)

    // No payUrl passed — mode:'invoice' resolves its own Stripe pay links server-side.
    B.sendInvoiceEmail({
      projectId: hold.projectAirtableId, invoiceId: hold.invoiceId, mode: 'invoice',
      invoiceNumber: hold.invoiceNumber, isReminder: true, send: false,
    }).then(function (preview) {
      var pv = $('#aj-billing-remind-preview')
      var sendBtn = root.querySelector('[data-action="send-billing-remind"]')
      if (!preview || preview.error) {
        if (pv) pv.textContent = 'Could not build a preview' + (preview && preview.error ? (': ' + preview.error) : '.')
        return
      }
      if (pv) {
        pv.innerHTML = '<div class="aj-modal-label" style="margin-bottom:4px">To</div><div style="font-size:12px;margin-bottom:10px">' + (preview.to || '(no recipient on file)') + '</div>'
          + '<div class="aj-modal-label" style="margin-bottom:4px">Subject</div><div style="font-size:12px;margin-bottom:10px">' + (preview.subject || '') + '</div>'
          + '<div class="aj-modal-label" style="margin-bottom:4px">Message</div>'
          + '<div style="background:var(--bg);border-radius:6px;padding:10px 12px;font-size:11px;color:var(--t1);line-height:1.6;white-space:pre-line;border-left:3px solid var(--t1)">' + (preview.text || '') + '</div>'
          + (preview.attachments && preview.attachments.length ? '<div style="font-size:10px;color:var(--t3);margin-top:8px">Attaching: ' + preview.attachments.map(function (a) { return a.filename }).join(', ') + '</div>' : '')
          + (!preview.enabled ? '<div style="font-size:10px;color:var(--amb);margin-top:8px">Email sending is not yet switched on — clicking Send will show the preview without emailing.</div>' : '')
      }
      if (sendBtn && preview.to) sendBtn.removeAttribute('disabled')
    }).catch(function () {
      var pv2 = $('#aj-billing-remind-preview'); if (pv2) pv2.textContent = 'Could not build a preview.'
    })
  }

  async function sendBillingReminder(holdIdx) {
    var hold = state.billingHolds[holdIdx]
    var statusEl = $('#aj-billing-remind-status')
    if (!hold) return
    if (statusEl) statusEl.textContent = 'Sending…'
    try {
      var res = await B.sendInvoiceEmail({
        projectId: hold.projectAirtableId, invoiceId: hold.invoiceId, mode: 'invoice',
        invoiceNumber: hold.invoiceNumber, isReminder: true, send: true,
      })
      if (res && res.sent) {
        if (statusEl) statusEl.textContent = 'Sent.'
        setTimeout(function () {
          var modal = root.querySelector('#aj-billing-remind-modal')
          if (modal) modal.parentNode.removeChild(modal)
          fillBillingHolds()
        }, 900)
      } else if (res && res.skipped) {
        if (statusEl) statusEl.textContent = 'Not sent — email sending is not switched on yet.'
      } else {
        if (statusEl) statusEl.textContent = 'Error: ' + ((res && res.error) || 'send failed')
      }
    } catch (e) {
      if (statusEl) statusEl.textContent = 'Send failed.'
    }
  }

  // ── Spinner ──────────────────────────────────────────────────────────────────
  function showSpinner(label, sub) {
    var existing = root.querySelector('#aj-spinner-overlay')
    if (existing) existing.parentNode.removeChild(existing)
    var el = document.createElement('div')
    el.id = 'aj-spinner-overlay'; el.className = 'aj-spinner-overlay'
    el.innerHTML = '<div class="aj-spinner-box"><div class="aj-spinner"></div><div class="aj-spinner-label">' + (label || 'Working...') + '</div>' + (sub ? '<div class="aj-spinner-sub">' + sub + '</div>' : '') + '</div>'
    root.appendChild(el)
  }
  function updateSpinner(label, sub) {
    var lbl = root.querySelector('.aj-spinner-label'), s = root.querySelector('.aj-spinner-sub')
    if (lbl) lbl.textContent = label || ''
    if (s) s.textContent = sub || ''
  }
  function hideSpinner() {
    var el = root.querySelector('#aj-spinner-overlay')
    if (el) el.parentNode.removeChild(el)
  }

  // ── Create run ───────────────────────────────────────────────────────────────
  async function createRun() {
    var my = getCurrentMonthYear()
    // Hard block: re-check the prior month's run status here too, not just the button render — a
    // stale render or a double-click must not bypass the gate.
    if (state.priorRun && state.priorRun.fields['Status'] !== 'Complete') {
      alertDialog({ title: 'Close out ' + (state.priorRun.fields['Month'] || 'the prior run') + ' first', message: 'The ' + (state.priorRun.fields['Month'] || 'previous') + ' billing run isn\'t closed out yet. Open it and close it out before starting ' + my.month + '.' })
      return
    }
    showSpinner('Creating ' + my.month + ' billing run', 'Setting up run record...')
    try {
      var run = await B.createRun({ Month: my.month, Year: my.year, Status: 'In Progress' })
      if (!run || !run.id) throw new Error('run create failed')
      updateSpinner('Finding active projects', 'Checking executed contracts + open AR...')
      var projects = await B.getBillingRunProjects()
      if (!projects || !projects.length) { hideSpinner(); load(); return }
      for (var i = 0; i < projects.length; i++) {
        updateSpinner('Creating line items', (i + 1) + ' of ' + projects.length + ' projects')
        try {
          await B.createLineItem({
            Project: [projects[i].id],
            'Billing Runs': [run.id],
            'Current Project Retainer': projects[i].fields['Current Project Retainer'] || 0,
          })
        } catch (e) { /* keep going */ }
      }
      updateSpinner('Almost done', 'Loading your billing run...')
      hideSpinner(); load()
    } catch (err) {
      hideSpinner()
      var inner = $('#aj-ready-inner')
      if (inner) inner.insertAdjacentHTML('afterbegin', '<div class="aj-error">Failed to create billing run. Please try again.</div>')
    }
  }

  // ── Remind modal ───────────────────────────────────────────────────────────────
  function showRemindModal(memberId, memberName, daysAgo) {
    var existing = root.querySelector('#aj-remind-modal')
    if (existing) existing.parentNode.removeChild(existing)

    var member = null
    for (var i = 0; i < state.members.length; i++) { if (state.members[i].id === memberId) { member = state.members[i]; break } }
    var stats = member ? getMemberStats(memberId) : null
    var daysText = daysAgo > 0 ? daysAgo + ' day' + (daysAgo > 1 ? 's' : '') + ' ago' : 'today'
    var my = getCurrentMonthYear()

    var previewLines = []
    previewLines.push('Action needed: time entries')
    previewLines.push('Hi ' + memberName + ' - I am reviewing time entries for the ' + my.month + ' billing run and yours need attention before we can close. Please get caught up as soon as you can so we can issue invoices on time.')
    if (stats) {
      var sp = [stats.hours.toFixed(1) + 'h of ' + stats.expected + 'h submitted']
      if (stats.missingDays > 0) sp.push(stats.missingDays + ' day' + (stats.missingDays > 1 ? 's' : '') + ' missing')
      if (stats.pending > 0) sp.push(stats.pending + ' pending review')
      if (stats.rejected > 0) sp.push(stats.rejected + ' rejected')
      previewLines.push('Hours: ' + sp.join(' · '))
    }
    previewLines.push('Sent by Alder James billing run - ' + my.month + ' ' + my.year)
    var slackPreview = previewLines.join('\n')

    var el = document.createElement('div')
    el.id = 'aj-remind-modal'; el.className = 'aj-modal-overlay'
    el.innerHTML = '<div class="aj-modal">'
      + '<div class="aj-modal-head"><div class="aj-modal-title">Remind ' + memberName + '</div><button class="aj-modal-close" data-action="close-modal">x</button></div>'
      + '<div class="aj-modal-body">'
      + '<div class="aj-modal-label" style="margin-bottom:4px">Slack message preview</div>'
      + '<div style="background:var(--bg);border-radius:6px;padding:10px 12px;font-size:11px;color:var(--t1);line-height:1.7;margin-bottom:10px;border-left:3px solid var(--t1);white-space:pre-line" id="aj-slack-preview">' + slackPreview + '</div>'
      + '<div class="aj-modal-label">Edit note (optional)</div>'
      + '<textarea class="aj-modal-textarea" id="aj-remind-msg" style="min-height:70px" placeholder="Add a personal note to append to the message..."></textarea>'
      + '</div>'
      + '<div class="aj-modal-footer">'
      + '<span class="aj-modal-status" id="aj-remind-status"></span>'
      + '<button class="aj-btn" data-action="close-modal">Cancel</button>'
      + '<button class="aj-btn aj-btn-p" data-action="send-remind" data-member-id="' + memberId + '">Send via Slack</button>'
      + '</div></div>'
    root.appendChild(el)
  }

  async function sendRemind(memberId) {
    var msgEl = $('#aj-remind-msg'), statusEl = $('#aj-remind-status')
    if (!msgEl) return
    var member = null
    for (var i = 0; i < state.members.length; i++) { if (state.members[i].id === memberId) { member = state.members[i]; break } }
    if (!member) { if (statusEl) statusEl.textContent = 'Member not found.'; return }

    var slackIds = member.fields['Slack Member ID (from Contacts)']
    var slackId = slackIds && slackIds.length ? slackIds[0] : null
    if (!slackId) { if (statusEl) statusEl.textContent = 'No Slack ID on file for this member.'; return }

    if (statusEl) statusEl.textContent = 'Sending...'
    var previewEl = $('#aj-slack-preview')
    var baseMsg = previewEl ? previewEl.textContent : ''
    var note = msgEl.value.trim()
    var fullText = note ? (baseMsg + '\n\n' + note) : baseMsg

    var my = getCurrentMonthYear()
    var memberName = member.fields['Member Name'] || ''
    var stats = getMemberStats(memberId)
    var bodyText = 'Hi *' + memberName + '* - I am reviewing time entries for the *' + my.month + '* billing run and yours need attention before we can close. Please get caught up as soon as you can so we can issue invoices on time.' + (note ? '\n\n' + note : '')
    var blocks = [
      { type: 'header', text: { type: 'plain_text', text: 'Action needed: time entries', emoji: true } },
      { type: 'section', text: { type: 'mrkdwn', text: bodyText } },
      { type: 'divider' },
      { type: 'section', fields: [
        { type: 'mrkdwn', text: '*Hours submitted*\n' + stats.hours.toFixed(1) + 'h of ' + stats.expected + 'h expected' },
        { type: 'mrkdwn', text: '*Issues*\n' + (stats.missingDays > 0 ? stats.missingDays + ' day' + (stats.missingDays > 1 ? 's' : '') + ' missing  ' : '') + (stats.pending > 0 ? stats.pending + ' pending  ' : '') + (stats.rejected > 0 ? stats.rejected + ' rejected' : '') + (stats.missingDays === 0 && stats.pending === 0 && stats.rejected === 0 ? 'None' : '') }
      ]},
      { type: 'context', elements: [ { type: 'mrkdwn', text: 'Sent via Alder James billing run - ' + my.month + ' ' + my.year } ] }
    ]

    try {
      var res = await B.remindMember({ slackUserId: slackId, text: fullText, blocks: blocks })
      if (res && res.ok) {
        if (statusEl) statusEl.textContent = 'Sent.'
        setTimeout(function() {
          var modal = root.querySelector('#aj-remind-modal')
          if (modal) modal.parentNode.removeChild(modal)
          state.reminded[memberId] = true
          render(state.run)
        }, 1200)
      } else {
        if (statusEl) statusEl.textContent = 'Error: ' + ((res && res.error) || 'unknown')
      }
    } catch (e) {
      if (statusEl) statusEl.textContent = 'Send failed.'
    }
  }

  function onClick(e) {
    var t = e.target
    var action = t.getAttribute('data-action')
    if (!action) return
    if (action === 'create-run') createRun()
    if (action === 'open-run') {
      window.dispatchEvent(new CustomEvent('open-billing', { detail: { tool: 'run', runId: t.getAttribute('data-run-id') || '' } }))
      return
    }
    if (action === 'remind-member') {
      showRemindModal(t.getAttribute('data-member-id'), t.getAttribute('data-member-name'), parseInt(t.getAttribute('data-days') || '0', 10))
    }
    if (action === 'close-modal') {
      var modal = root.querySelector('#aj-remind-modal')
      if (modal) modal.parentNode.removeChild(modal)
    }
    if (action === 'send-remind') sendRemind(t.getAttribute('data-member-id'))
    if (action === 'open-billing-remind') showBillingReminderModal(parseInt(t.getAttribute('data-hold-idx'), 10))
    if (action === 'close-billing-remind-modal') {
      var brm = root.querySelector('#aj-billing-remind-modal')
      if (brm) brm.parentNode.removeChild(brm)
    }
    if (action === 'send-billing-remind') sendBillingReminder(parseInt(t.getAttribute('data-hold-idx'), 10))
  }

  root.addEventListener('click', onClick)
  load()

  return function teardown() {
    root.removeEventListener('click', onClick)
  }
}
